import type { BifrostWeb } from '@seekrtech/bifrost-web'
import type { Result } from '../types'

/**
 * One local push notification to schedule. Text is resolved from native l10n
 * keys when given (re-resolved on the device's current language at fire
 * time); plain `title` / `body` strings are baked at schedule time and serve
 * as the fallback for app versions that lack the key.
 */
export interface NotificationItem {
  /**
   * Unique within the group and stable across calls — never derived from
   * time, randomness, or localized text. `[a-z0-9_-]` only.
   */
  key: string
  title_key?: string
  title_args?: string[]
  body_key?: string
  body_args?: string[]
  title?: string
  body?: string
  /** Absolute fire time in epoch seconds; must be in the future. */
  fire_at: number
  /** App deeplink opened on tap; native treats it as opaque. */
  tap_url?: string
  /** `notification_type` for native analytics. */
  tracking_type?: string
}

/** An item native actually scheduled, with the instant it will fire. */
export interface ScheduledNotification {
  key: string
  fire_at: number
}

export type NotificationServiceError =
  /** Notification permission not granted. */
  | 'permission_denied'
  /** Unreadable payload, bad key, unreasonable time, or no text at all. */
  | 'invalid_request'
  /** Over the cap: 5 items per group, 10 web-scheduled items in total. */
  | 'quota_exceeded'
  /** This build explicitly refuses this group (unknown groups are allowed). */
  | 'unsupported'

export interface NotificationServiceMethods {
  /**
   * Replaces the whole group with `items`, idempotently. There is no cancel:
   * clear a group by scheduling it with no items. Only the items native
   * really scheduled come back.
   */
  schedule: (payload: {
    group: string
    items: NotificationItem[]
  }) => Promise<
    Result<{ scheduled: ScheduledNotification[] }, NotificationServiceError>
  >
  /** What native currently has scheduled for the group (fact, not intent). */
  getScheduled: (payload: {
    group: string
  }) => Promise<
    Result<{ scheduled: ScheduledNotification[] }, NotificationServiceError>
  >
}

export function createNotificationService(
  bifrost: BifrostWeb,
): NotificationServiceMethods {
  return bifrost.createNativeProxy<NotificationServiceMethods>(
    'NotificationService',
  )
}
